import * as React from 'react'
import {
  AlertTriangle,
  ArrowDown,
  ArrowDownToLine,
  Check,
  Copy,
  Download,
  Search,
  Terminal,
  WrapText,
  X,
} from 'lucide-react'
import {
  RUN_PHASE_LABELS,
  type RunLogLineDto,
  type RunMetricsDto,
  type RunPhase,
  type RunStateDto,
} from '@acs/shared'
import { cn } from '@/lib/cn'
import { useLiveElapsed } from '@/hooks/use-relative-time'
import { Badge, type Tone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RunPipeline } from '@/components/session/run-pipeline'

/**
 * The live console for one run.
 *
 * Two halves: a counter strip that answers "is this healthy?" without reading a
 * line, and the log itself.
 *
 * ── The counters are given, not derived ─────────────────────────────
 * Every number in the strip arrives on a `run.state` frame, already accumulated
 * server-side from structured fields. The console does not scan the log to
 * produce them. That matters twice over: the previous generation of this screen
 * pattern-matched status codes out of message TEXT — so rewording a log line
 * silently zeroed a tile — and it re-scanned the whole ring on every repaint,
 * which is O(lines) work on a component that repaints whenever a line arrives.
 * Here a repaint reads eleven numbers off an object.
 *
 * ── Why this is not virtualised ─────────────────────────────────────
 * The list is capped at 300 lines client-side and the server's ring is 150, so
 * the DOM is bounded by construction and there is nothing to virtualise. Rows carry
 * `content-visibility: auto`, which lets the browser skip layout and paint for
 * the ones scrolled out of view — the same win a virtualizer buys, with no
 * library, no ResizeObserver per row, and lines that wrap naturally instead of
 * being forced to a fixed height a virtualizer could trust.
 */

/* ================================================================== *
 * Phase
 * ================================================================== */

const PHASE_TONE: Readonly<Record<RunPhase, Tone>> = Object.freeze({
  idle: 'neutral',
  starting: 'info',
  signing_in: 'live',
  awaiting_otp: 'warn',
  applying: 'info',
  holding: 'ok',
  failed: 'danger',
  stopped: 'neutral',
})

/** Phases where work is genuinely in flight, so the pill should pulse. */
const PHASE_ACTIVE: ReadonlySet<RunPhase> = new Set<RunPhase>([
  'starting',
  'signing_in',
  'awaiting_otp',
  'applying',
])

export function RunPhasePill({ phase, className }: { phase: RunPhase; className?: string }) {
  const active = PHASE_ACTIVE.has(phase)
  return (
    <span className={className}>
      <Badge tone={PHASE_TONE[phase]} dot={!active && phase !== 'holding'} pulse={active}>
        {RUN_PHASE_LABELS[phase]}
      </Badge>
    </span>
  )
}

/* ================================================================== *
 * Formatting
 * ================================================================== */

const NUM = new Intl.NumberFormat('en-US')

/** A zero reads as `—`. Nothing draws the eye like a row of zeroes. */
function dash(n: number): string {
  return n > 0 ? NUM.format(n) : '—'
}

function ms(v: number | null): string {
  if (v === null) return '—'
  return v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`
}

// No `duration()` helper: session time is the one elapsed value on this screen
// and it is rendered by `useLiveElapsed`, which owns its own formatting because
// it has to produce a new string every second.

/**
 * `hh:mm:ss.mmm`, local.
 *
 * The milliseconds are not decoration. Several of the calls on this screen
 * happen inside the same second — a WAF challenge and the retry behind it, a
 * claim and the schedule read that triggered it — and truncating to the second
 * renders them as simultaneous, which is exactly the ordering an operator opens
 * the log to establish.
 */
function clockTime(at: number): string {
  const d = new Date(at)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(
    d.getMilliseconds()
  ).padStart(3, '0')}`
}

/** `create_application` → `create application`. */
function stepLabel(step: string): string {
  return step.replace(/_/g, ' ')
}

/**
 * Colour for an HTTP status in the log's right-hand rail.
 *
 * 405 is deliberately not `danger`. From these hosts it is not an error page, it
 * IS the WAF challenge — an expected step with a solve behind it — and painting
 * it the same red as a 403 would train the eye to alarm at the routine.
 */
/**
 * When a duration in this log is worth colouring.
 *
 * ── Why one scale, set high ─────────────────────────────────────────
 * This column carries two very different things: HTTP round trips, where 326ms
 * is normal, and whole STEPS, where a browser launch legitimately takes one to
 * thirteen seconds. Scored at 1s/3s the browser line came out amber on every run
 * — a correct step, permanently flagged — which is the fastest way to teach an
 * operator that amber in this column means nothing.
 *
 * So the bands sit where BOTH kinds are genuinely wrong rather than where the
 * faster kind is merely unusual: nothing under two seconds is coloured, and past
 * five something has gone badly enough that it is worth interrupting for. The
 * cost is that a 1.5s update-application reads as ordinary; that is the right
 * trade, because the alternative is a column of amber nobody reads.
 */
const CALL_SLOW_MS = 2_000
const CALL_BAD_MS = 5_000

function statusTone(status: number): string {
  if (status >= 200 && status < 300) return 'text-ok'
  if (status === 405 || status === 429) return 'text-warn'
  return 'text-danger'
}

/* ================================================================== *
 * Counter strip
 * ================================================================== */

type TileTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'accent'

const TILE_VALUE: Readonly<Record<TileTone, string>> = Object.freeze({
  neutral: 'text-fg',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  accent: 'text-accent',
})

const TILE_EDGE: Readonly<Record<TileTone, string>> = Object.freeze({
  neutral: 'border-l-line',
  ok: 'border-l-ok',
  warn: 'border-l-warn',
  danger: 'border-l-danger',
  accent: 'border-l-accent',
})

interface Tile {
  key: string
  label: string
  value: string
  tone: TileTone
  help: string
}

/**
 * The tile grid: as many per row as fit, never a fixed column count.
 *
 * `lg:grid-cols-5` decided the tile width from the column count rather than from
 * the content, so a four-character value sat in a tile twice as wide as it
 * needed — two thirds of the strip was padding, in a panel that is half a screen
 * wide. `auto-fill` inverts it: the tile states the narrowest it can usefully
 * be, the browser fits as many as the width allows, and adding an eleventh tile
 * costs nothing instead of silently creating a new row.
 */
const TILE_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(6.25rem,1fr))] gap-1'

const CounterTile = React.memo(function CounterTile({ tile }: { tile: Tile }) {
  return (
    <div
      title={tile.help}
      /*
       * A real `<dl>` pair, not two anonymous spans.
       *
       * A screen reader reading two spans announces "Calls 8" as one run-on
       * string with nothing saying which half is the name and which is the
       * value. A definition list says it in the one way that needs no
       * convention, and costs nothing to render.
       */
      className={cn(
        'flex min-w-0 cursor-default flex-col gap-[3px] rounded-md border border-line border-l-2 bg-s1 px-2 py-1',
        'transition-colors hover:bg-s2',
        TILE_EDGE[tile.tone]
      )}
    >
      <dt className="truncate text-[9.5px] font-medium tracking-[0.06em] text-faint uppercase">
        {tile.label}
      </dt>
      {/* `tabular` keeps the strip from jittering sideways as counts tick. */}
      <dd className={cn('tabular truncate font-mono text-[14px] leading-none', TILE_VALUE[tile.tone])}>
        {tile.value}
      </dd>
    </div>
  )
})

/**
 * Which counters get a colour, and when.
 *
 * Only genuinely bad numbers take one, so a single glance finds the problem. A
 * 403 or a 429 is the WAF pushing back and always worth seeing; a 405 is the
 * shape of the request being wrong and is worse. Everything else stays neutral
 * however large it grows — a high call count is just a long run.
 */
function buildTiles(state: RunStateDto): Tile[] {
  const m: RunMetricsDto = state.metrics
  const failed = m.s403 + m.s405 + m.s429 + m.otherErrors

  return [
    {
      key: 'calls',
      label: 'Calls',
      value: dash(m.calls),
      tone: 'neutral',
      help: 'update-application attempts: the first confirm, every 60s heartbeat, and every strike. Counted per attempt, so a 405 solved and retried into a 200 is two.',
    },
    {
      key: 'ok',
      label: '2xx OK',
      value: dash(m.ok2xx),
      // Green only when it is the whole story. A run with successes AND
      // rejections should not read as healthy at a glance.
      tone: m.ok2xx > 0 && failed === 0 ? 'ok' : 'neutral',
      help: 'update-application calls the upstream accepted. Each one re-holds the shift for another 60 seconds.',
    },
    {
      key: 's403',
      label: '403',
      value: dash(m.s403),
      tone: m.s403 > 0 ? 'danger' : 'neutral',
      help: 'Forbidden — the session was refused. Usually a token that is no longer accepted; the run signs in again on its own.',
    },
    {
      key: 's405',
      label: '405',
      value: dash(m.s405),
      tone: m.s405 > 0 ? 'warn' : 'neutral',
      help: 'A WAF challenge, not an error. The body IS the puzzle; it is solved and the call retried. 405→200 below is what that cost.',
    },
    {
      key: 's429',
      label: '429',
      value: dash(m.s429),
      tone: m.s429 > 0 ? 'warn' : 'neutral',
      help: 'Rate limited. Backing off is automatic; a rising count is not normal.',
    },
    {
      key: 'last-rt',
      label: 'Last RT',
      value: ms(m.lastOkRttMs),
      // A round trip past two seconds through an ISP proxy is the early signal
      // that the endpoint is struggling, well before anything actually fails.
      tone: m.lastOkRttMs !== null && m.lastOkRttMs > CALL_SLOW_MS ? 'warn' : 'neutral',
      help: 'Round-trip of the most recent ACCEPTED update-application call. Refusals are excluded: a WAF block is answered at the edge in milliseconds and would read as a healthy number on a failing run.',
    },
    {
      key: 'mean-rt',
      label: 'Mean RT',
      value: ms(m.meanRttMs),
      tone: m.meanRttMs !== null && m.meanRttMs > CALL_SLOW_MS ? 'warn' : 'neutral',
      help: 'Average round-trip of accepted update-application calls. This is the baseline a claim will pay: the heartbeat makes exactly this call, on exactly this connection, every 60 seconds — so its average is the best estimate of what the strike costs when a shift opens. A rising mean is the endpoint degrading, visible long before anything fails.',
    },
    {
      key: 'waf-recovery',
      label: '405→200',
      value: ms(m.waf405ToOkMs),
      /*
       * A solve costs seconds, and seconds lose shifts.
       *
       * There is no "good" value here, only a survivable one: anything past a
       * second means a challenge met during a claim would have cost the seat, so
       * the threshold is set where it stops being recoverable rather than where
       * it stops being fast.
       */
      tone:
        m.waf405ToOkMs === null ? 'neutral' : m.waf405ToOkMs > 1_500 ? 'danger' : 'warn',
      help: 'From the last WAF challenge to the 200 that followed it — what answering it actually cost. Blank until one is met.',
    },
    {
      key: 'claim',
      label: 'ACTIVE→200',
      value: ms(m.activeToOkMs),
      /*
       * The number the entire system exists to make small.
       *
       * Detection, decision and the strike's round trip, end to end, from a
       * worker seeing the schedule open to the seat being confirmed. Accented
       * rather than coloured by threshold: it is not a warning, it is the score.
       */
      tone: m.activeToOkMs === null ? 'neutral' : m.activeToOkMs > 2_000 ? 'warn' : 'accent',
      help: 'From a worker seeing ACTIVE to the accepted claim: detection, decision and the strike, end to end. The one number that says whether the race was won quickly. Blank until a shift opens.',
    },
  ]
}

export function RunCounters({ state }: { state: RunStateDto }) {
  const tiles = React.useMemo(() => buildTiles(state), [state])

  return (
    <div className="space-y-1.5">
      {/*
        No session clock here any more — it is in the drawer header.

        It was the odd one out: every other tile counts what this run has DONE,
        while that one reports the age of the credential behind it, which is a
        fact about the session rather than about the calls. Beside nine call
        counters it read as trivia; beside the customer it names, it says how
        much life the session has left.
      */}
      <dl aria-label="update-application counters" className={TILE_GRID}>
        {tiles.map((t) => (
          <CounterTile key={t.key} tile={t} />
        ))}
      </dl>
      <RunIdentity state={state} />
    </div>
  )
}

/**
 * The identifiers, on one quiet line.
 *
 * These are what an operator needs when one customer fails and the rest do not —
 * which endpoint it left from, which application it holds — and they are exactly
 * the things nobody wants taking up a tile until that happens.
 */
function RunIdentity({ state }: { state: RunStateDto }) {
  const parts: { label: string; value: string }[] = []

  if (state.egress) parts.push({ label: 'egress', value: state.egress })
  if (state.applicationId) {
    parts.push({ label: 'application', value: state.applicationId })
  }
  if (state.applicationState) parts.push({ label: 'state', value: state.applicationState })
  /*
   * The pair this run chose for itself, and why.
   *
   * Labelled `auto` rather than `job` because that is the fact worth carrying:
   * these ids are NOT on the customer's record and never will be, so an operator
   * who looks the customer up will not find them.
   */
  if (state.autoTarget) {
    const t = state.autoTarget
    /*
     * And the shift's seat count, as the schedule list has it now. Absent on
     * a frame that predates the field; "no count" when the list has stopped
     * reporting the shift, which is a different fact from zero.
     */
    const seats =
      t.seats === undefined
        ? ''
        : t.seats === null
          ? ' · no seat count'
          : ` · ${t.seats} seat${t.seats === 1 ? '' : 's'} open`
    parts.push({
      label: 'auto',
      value: `${t.jobId} · ${t.scheduleId} · ${t.siteId} · ${t.scheduleType}${seats}`,
    })
  }
  if (state.runId) parts.push({ label: 'run', value: state.runId.slice(0, 8) })

  if (parts.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-0.5 text-[11px]">
      {parts.map((p) => (
        <span key={p.label} className="flex min-w-0 items-baseline gap-1">
          <span className="text-faint">{p.label}</span>
          <span className="truncate font-mono text-muted">{p.value}</span>
        </span>
      ))}
    </div>
  )
}

/* ================================================================== *
 * Log
 * ================================================================== */

/**
 * The log filters, one per level plus two aggregates.
 *
 * `problems` is kept alongside the individual levels rather than replaced by
 * them, because "show me everything wrong" is a single click an operator wants
 * constantly and `warn`+`error` are two. The per-level chips answer the other
 * question — "was that warning the only one, or the tenth" — which a combined
 * count cannot.
 */
type LevelFilter = 'all' | 'problems' | RunLogLineDto['level']

/** Chip order, left to right. Aggregates first, then severity ascending. */
const LEVEL_FILTERS: readonly LevelFilter[] = ['all', 'problems', 'info', 'success', 'warn', 'error']

const FILTER_LABEL: Readonly<Record<LevelFilter, string>> = Object.freeze({
  all: 'All',
  problems: 'Problems',
  info: 'Info',
  success: 'Success',
  warn: 'Warn',
  error: 'Error',
})

/**
 * Which chips get a coloured count, and only when they have something in them.
 *
 * A zero is deliberately toneless: colouring an empty Error chip red trains the
 * eye to see alarm where there is none, which is how a genuine one stops being
 * noticed.
 */
const FILTER_TONE: Readonly<Partial<Record<LevelFilter, 'warn' | 'danger' | 'ok'>>> = Object.freeze({
  problems: 'warn',
  success: 'ok',
  warn: 'warn',
  error: 'danger',
})

/**
 * The steps an operator thinks in, in the order a run performs them.
 *
 * GROUPS, not a per-key label map — which was the bug in the first version of
 * this filter. Sign-in is four step keys (`csrf`, `sign_in`, `confirm_otp`,
 * `authorize`), and mapping each to the string "Sign-in" while keying the chips
 * by step produced four chips all reading "Sign-in", each filtering to a
 * different quarter of the same phase. Grouping is the fix: one chip, one idea,
 * every key it covers.
 *
 * The order is the RUN's order rather than first-seen. First-seen looks stable
 * and is not — it depends on which call happened to log first, so the same
 * console could arrange the same phases differently on two runs, and a control
 * whose layout moves is a control nobody builds muscle memory for.
 */
const STEP_GROUPS: readonly { label: string; keys: readonly string[] }[] = Object.freeze([
  { label: 'Browser', keys: ['browser'] },
  { label: 'Sign-in', keys: ['csrf', 'sign_in', 'confirm_otp', 'authorize'] },
  { label: 'OTP', keys: ['otp_wait'] },
  // Its own chip rather than folded into Create: a run can sit here for hours,
  // and "why has nothing happened since sign-in" is the question it answers.
  { label: 'Match', keys: ['match_schedule'] },
  { label: 'Create', keys: ['create_application'] },
  { label: 'Update app', keys: ['update_application'] },
  { label: 'Keep-alive', keys: ['keep_alive'] },
  { label: 'Watch', keys: ['watch_schedule'] },
  /*
   * One chip for the whole form wizard, not five.
   *
   * Its five keys exist so the API can attribute a failure to the call that
   * caused it — a save, an advance, a slot check — but an operator filtering the
   * log wants "show me the application being finished", which is one idea. The
   * distinction lives in the message text, where it is read rather than clicked.
   */
  {
    label: 'Finish form',
    keys: [
      'onboarding_step',
      'onboarding_save',
      'onboarding_advance',
      'onboarding_nhe',
      'onboarding_done',
    ],
  },
  /*
   * The socket gets its own chip rather than joining the one above.
   *
   * Every other line in the wizard is an HTTP call; these are the frames that
   * decide which call comes next. When a run stops early the question is almost
   * always "what did the server last say", and that is one click rather than
   * reading past fifteen PUTs to find it.
   */
  { label: 'Socket', keys: ['onboarding_socket'] },
])

/** key → group label, built once. Unknown keys fall back to the humanised key. */
const STEP_LABEL: ReadonlyMap<string, string> = new Map(
  STEP_GROUPS.flatMap((g) => g.keys.map((k) => [k, g.label] as const))
)

function stepChip(step: string): string {
  return STEP_LABEL.get(step) ?? stepLabel(step)
}

/**
 * Everything one line is searchable by, lowercased ONCE and remembered.
 *
 * The search covers the response body, which is the only place an upstream
 * `errorCode`, an application id or a state string actually appears — a filter
 * that could not see them would be quietly lying about what the log contains.
 * But building that string per line per keystroke means lowercasing the entire
 * buffer's worth of bodies on every character typed, on the main thread, while
 * the drawer is also taking a live push every 80ms.
 *
 * A `WeakMap` keyed on the line object is the right cache here: log lines are
 * immutable once appended and are only ever added to the front or dropped off
 * the end, so a hit is the common case and an evicted line takes its entry with
 * it — no size bound to choose and nothing to invalidate.
 */
const HAYSTACK = new WeakMap<RunLogLineDto, string>()

/**
 * Separator between the fields, and it is not a space.
 *
 * The old filter tested each field independently, so a query could never match
 * across two of them. Joining with a space quietly changes that: "ok 200" would
 * match a line whose `smallDetail` ends in "ok" and whose status is 200, which
 * is a hit the operator did not ask for. A newline cannot be typed into the
 * filter input, so concatenating cannot invent matches.
 */
const FIELD_SEP = String.fromCharCode(10)

function haystack(l: RunLogLineDto): string {
  const hit = HAYSTACK.get(l)
  if (hit !== undefined) return hit
  const built = [
    l.message,
    l.stepKey ?? '',
    typeof l.api === 'string' ? l.api : '',
    l.smallDetail ?? '',
    typeof l.detail === 'string' ? l.detail : '',
    typeof l.httpStatus === 'number' ? String(l.httpStatus) : '',
  ]
    .join(FIELD_SEP)
    .toLowerCase()
  HAYSTACK.set(l, built)
  return built
}

/** The sentinel for "every step", so the group has one value type throughout. */
const ALL_STEPS = '*'

/**
 * The sentinel for "every call to update-application", whatever step made it.
 *
 * Sits in the step group because it is the same kind of choice — one lens over
 * the log, mutually exclusive with the others — but it selects on the ENDPOINT
 * rather than on the phase, and that distinction is the whole reason it exists.
 *
 * `update-application` is the only request in the system that changes anything,
 * and three different phases send it: the claim fires it at a shift (step
 * `update_application`), the heartbeat sends the same PUT once a minute to hold
 * one (`keep_alive`), and the form wizard uses it to save (`onboarding_save`).
 * Filtering by step therefore cannot answer "show me every update call" — the
 * lines are spread across three chips, and the two the operator most wants
 * side by side, the claim and the beat, are the two furthest apart.
 *
 * Not a `*`-prefixed string, and not a plain word: this shares an id space with
 * step-group LABELS, and a group called "Update calls" arriving from the server
 * one day must not silently take over this chip.
 */
const UPDATE_CALLS = '@update-application'

/** Matched against `api`, which is `PUT /candidate-application/update-application`. */
const UPDATE_PATH_MARK = 'update-application'

/** Which outcome of an update call to show. `all` is every one of them. */
type UpdateCode = 'all' | 'failed_update' | 'not_available'

/**
 * The two codes worth a chip, lowercased for the haystack.
 *
 * Matched against the whole searchable line rather than a field of its own,
 * because that is where an `errorCode` actually lives: these endpoints answer a
 * refusal with HTTP 200 and a code in the BODY, which reaches the console as
 * `detail`. There is no status to filter on — that is the entire difficulty
 * this chip removes.
 *
 * The claim stand-down's own lines deliberately do NOT match: the stand-down
 * counts HTTP 429/403/502 and ignores this code, so its lines name the statuses
 * and never the code.
 */
const UPDATE_CODE_MATCH: Readonly<Record<Exclude<UpdateCode, 'all'>, string>> = Object.freeze({
  failed_update: 'failed_update_application',
  not_available: 'selected_schedule_not_available',
})

/** Prose on the chip, the exact code in its tooltip. */
const UPDATE_CODE_LABEL: Readonly<Record<UpdateCode, string>> = Object.freeze({
  all: 'All',
  failed_update: 'Failed update',
  not_available: 'Schedule not available',
})

const UPDATE_CODE_TITLE: Readonly<Record<UpdateCode, string>> = Object.freeze({
  all: 'Every call to update-application — the claim, the keep-alive beat and the form saves',
  failed_update:
    'errorCode FAILED_UPDATE_APPLICATION — the endpoint refusing to accept the update at all',
  not_available:
    'errorCode SELECTED_SCHEDULE_NOT_AVAILABLE — the shift was gone by the time we asked',
})

/**
 * Whether this line describes a call to update-application.
 *
 * Read off `api`, which the server sets from the request it actually made, and
 * never off the step or the message. A step is a phase and a message is prose;
 * both have been reworded, and neither is what the row is about.
 */
function isUpdateCall(l: RunLogLineDto): boolean {
  return typeof l.api === 'string' && l.api.toLowerCase().includes(UPDATE_PATH_MARK)
}

const LEVEL_MARK: Readonly<Record<RunLogLineDto['level'], { glyph: string; className: string }>> =
  Object.freeze({
    info: { glyph: '•', className: 'text-faint' },
    success: { glyph: '✓', className: 'text-ok' },
    warn: { glyph: '▲', className: 'text-warn' },
    error: { glyph: '✕', className: 'text-danger' },
  })

/**
 * When each line reached this screen, so a row can tell being WRITTEN from
 * being DRAWN.
 *
 * Only a line that arrives while the console is open plays the entrance.
 * Everything else — the history a drawer opens onto, a re-seed after a
 * reconnect, a burst from a tab that sat in the background, a filter bringing
 * old rows back — is stamped as long ago and renders still. Without that the
 * drawer would open onto six hundred rows lighting up at once, which is noise
 * rather than news.
 *
 * A `WeakMap` on the line object for the reason `HAYSTACK` is one: lines are
 * appended and never edited, and an evicted line takes its entry with it.
 */
const ARRIVED = new WeakMap<RunLogLineDto, number>()

/** How long after arriving a row still mounts with the entrance. */
const ENTER_MS = 1_000

/** More new lines than this in one update is history arriving, not a run writing. */
const ENTER_BATCH = 12

function noteArrivals(lines: readonly RunLogLineDto[]): void {
  let fresh = 0
  for (const l of lines) if (!ARRIVED.has(l)) fresh += 1
  if (fresh === 0) return
  // Every line new at once is a first load or a re-seed, never a run writing.
  const at = fresh === lines.length || fresh > ENTER_BATCH ? -Infinity : performance.now()
  for (const l of lines) if (!ARRIVED.has(l)) ARRIVED.set(l, at)
}

function isEntering(line: RunLogLineDto): boolean {
  return performance.now() - (ARRIVED.get(line) ?? -Infinity) < ENTER_MS
}

/**
 * One line.
 *
 * `content-visibility: auto` is the performance mechanism: the browser skips
 * layout and paint entirely for rows outside the scrollport, so a 300-line log
 * costs about what a 25-line one does. `contain-intrinsic-size` gives it a
 * height to assume for the skipped rows, which is what keeps the scrollbar
 * stable instead of jumping as rows are rendered and measured.
 */
const LogRow = React.memo(function LogRow({
  line,
  wrap,
  query,
}: {
  line: RunLogLineDto
  wrap: boolean
  query: string
}) {
  const mark = LEVEL_MARK[line.level]

  /*
   * Decided once, when the row mounts, and never again.
   *
   * The entrance is a CSS animation, and adding its class to a row that is
   * already on screen would start it there — so a later re-render (Wrap, a
   * search highlight, a re-seed handing over an equal line object) must not
   * be able to change the answer.
   */
  const [entering] = React.useState(() => isEntering(line))

  /*
   * `smallDetail` now always sits UNDER the message, and the right-hand column
   * is numbers only.
   *
   * It used to be split by length — short facts into a right-hand gutter, long
   * prose beneath — which meant the right edge of the log alternated between a
   * response time, a worker id, a sentence fragment and nothing, row by row.
   * Nothing lined up, so nothing could be scanned. The rail is now exactly two
   * fields wide, always the same two, and every row either has them or shows a
   * dash: that is what makes a column of statuses and round trips readable
   * downwards, which is the whole point of putting them in a column.
   */
  const detail = line.smallDetail
  const api = typeof line.api === 'string' && line.api !== '' ? line.api : null
  const status = typeof line.httpStatus === 'number' ? line.httpStatus : null
  const rtt = typeof line.ms === 'number' ? line.ms : null

  return (
    <div
      className={cn(
        'flex items-start gap-2 border-b border-line/40 px-2.5 py-[3px] last:border-b-0',
        line.level === 'error' && 'bg-danger-soft/40',
        line.level === 'warn' && 'bg-warn-soft/30',
        entering && 'log-line-in'
      )}
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 22px' }}
    >
      <span className="tabular shrink-0 text-faint">{clockTime(line.at)}</span>
      <span className={cn('w-3 shrink-0 text-center', mark.className)} aria-hidden="true">
        {mark.glyph}
      </span>

      {/*
        Always occupies its slot, even when empty.

        A conditional column shifts the message left on the few lines that have
        no step, which breaks the vertical edge every other row lines up to — and
        that edge is the only thing making a 300-line log scannable.
      */}
      <span
        className="w-[6.5rem] shrink-0 truncate text-muted"
        title={line.stepKey ?? undefined}
      >
        {line.stepKey ? stepChip(line.stepKey) : ''}
      </span>

      {/*
        The message column, and the ONLY thing allowed to take the leftover
        width.

        `min-w-0` is what makes `flex-1` actually shrinkable — without it a flex
        item's floor is its content width. The bug this replaces was the detail
        beside it: `smallDetail` carried `shrink-0`, and on an error line that
        detail is a full sentence of explanatory hint. A ~500px item that refuses
        to shrink starves everything to its left, so the message column collapsed
        to about one character and `break-words` dutifully wrapped the text one
        letter per line.
      */}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            'min-w-0',
            wrap ? 'break-words' : 'truncate',
            line.level === 'error'
              ? 'text-danger'
              : line.level === 'warn'
                ? 'text-warn'
                : line.status === 'ok'
                  ? 'text-fg'
                  : 'text-muted'
          )}
          title={wrap ? undefined : line.message}
        >
          <Highlight text={line.message} query={query} />
        </span>

        {/*
          The endpoint, then the context — one sub-line, always in that order.

          The endpoint comes first and is the only coloured thing here because it
          answers the question this row could not previously answer at all: WHICH
          call. The step label to the left names a phase of the run; three
          different phases send PUTs to update-application and one sends GETs to
          a different path entirely, so the label was never enough — and when the
          watch and the claim shared a label, it was actively wrong.
        */}
        {api !== null || detail !== null ? (
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
            {api !== null ? (
              <span className="tabular truncate text-accent/70" title={api}>
                {api}
              </span>
            ) : null}
            {detail !== null ? (
              <span className="min-w-0 break-words text-faint">
                <Highlight text={detail} query={query} />
              </span>
            ) : null}
          </span>
        ) : null}

        {/*
          The server's actual answer, folded away until asked for.

          A failed call's response body is the single most useful thing on the
          screen and also the longest, so it is neither hidden nor allowed to
          dominate: `<details>` keeps the row one line tall until the operator
          opens it, and `overflow-x-auto` keeps a long JSON line from widening
          the whole console.
        */}
        {line.detail ? (
          <details className="min-w-0 group">
            {/*
              The summary names what is inside, rather than saying "details".

              Every call now carries its response, not only the failures, so this
              control appears on most rows — and a column of identical
              "response details" labels tells the operator nothing about which one
              to open. Stating the status and the round trip makes the closed row
              informative on its own, and turns the log into something you scan
              rather than something you click through.

              "details" rather than "response" when there is no status, because
              some lines record a decision made HERE — no job assigned, nothing to
              apply to — and calling that a response would send the reader looking
              for a call that was never made.
            */}
            <summary
              className={cn(
                'cursor-pointer select-none marker:text-faint hover:text-muted',
                line.level === 'error' || line.level === 'warn' ? 'text-warn' : 'text-faint'
              )}
            >
              {typeof line.httpStatus === 'number'
                ? `response · ${line.httpStatus}${typeof line.ms === 'number' ? ` · ${NUM.format(line.ms)}ms` : ''}`
                : 'details'}
            </summary>
            <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded border border-line/60 bg-surface-2/60 p-2 text-[11px] leading-relaxed text-muted">
              {line.detail}
            </pre>
          </details>
        ) : line.level === 'warn' || line.level === 'error' ? (
          /*
            An absent detail on a PROBLEM line, said out loud.

            Every warn and error that describes a call now carries what the
            server answered — but "now" is a property of the call sites, and
            there are a dozen of them across four modules. A future one that
            forgets would render exactly like one that had nothing to report:
            silently, as a row with no disclosure triangle, and the operator
            would never learn there was something missing rather than something
            absent. Stating it turns a silent gap into a visible one.
          */
          <span className="text-faint italic">no response was recorded for this line</span>
        ) : null}
      </span>

      {/* The status code gets its own slot rather than being buried in the
          message, so a column of them can be scanned vertically. */}
      {status !== null ? (
        <span
          className={cn(
            'tabular w-[2.4rem] shrink-0 rounded-xs bg-s3/60 text-center text-[11px]',
            statusTone(status)
          )}
        >
          {status}
        </span>
      ) : (
        <span aria-hidden="true" className="hidden w-[2.4rem] shrink-0 sm:inline" />
      )}

      {/* ── The timing column ──────────────────────────────────────────
          Fixed width and right-aligned, so every duration in the log stacks
          into one scannable column. That is the difference between "I can see
          which call was slow" and "I have to read every line to find out".
          Coloured past the thresholds where a round trip through an ISP proxy
          stops being normal. */}
      <span
        className={cn(
          'tabular w-[4.2rem] shrink-0 text-right',
          rtt === null
            ? 'text-transparent'
            : rtt >= CALL_BAD_MS
              ? 'text-danger'
              : rtt >= CALL_SLOW_MS
                ? 'text-warn'
                : 'text-faint'
        )}
      >
        {rtt === null ? '·' : `${NUM.format(rtt)}ms`}
      </span>
    </div>
  )
})

/**
 * Mark the matched substring without `dangerouslySetInnerHTML`.
 *
 * Log messages carry upstream text, so building HTML from them would be an
 * injection path from whatever the remote service felt like returning.
 */
function Highlight({ text, query }: { text: string; query: string }) {
  if (query === '') return <>{text}</>

  const at = text.toLowerCase().indexOf(query.toLowerCase())
  if (at === -1) return <>{text}</>

  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-xs bg-accent-soft px-0.5 text-accent">
        {text.slice(at, at + query.length)}
      </mark>
      {text.slice(at + query.length)}
    </>
  )
}

/* ================================================================== *
 * Following the tail
 * ================================================================== */

/**
 * Keeping the newest line in view, and knowing when to stop.
 *
 * ── Why the first version kept switching itself off ─────────────────
 * It was one effect and one scroll listener: on every append, jump to the end;
 * on every scroll event, stop following unless the view was within 24px of the
 * end. The listener could not tell the operator's scrolling from its own, and
 * its own was rarely exact. Rows carry `content-visibility: auto`, so a row
 * that has never been drawn counts as 22px — and the jump measured those
 * guesses. The rows it landed on were then drawn at their real two or three
 * lines, the end moved further down, and the scroll event the jump itself had
 * queued arrived to find the view "scrolled away". Opening the drawer did
 * exactly that, which is why the toggle so often read off before anyone had
 * touched it.
 *
 * ── Only the operator changes the mode ──────────────────────────────
 * A wheel, a key, a finger or the scrollbar moving UP stops following; moving
 * back down to the end — or the toggle, the jump button, or End — resumes it.
 * The log growing, a row being drawn taller than its guess, the ring dropping
 * its oldest line: all of those move the scroll position, and none of them is
 * somebody looking away.
 *
 * ── It glides ───────────────────────────────────────────────────────
 * One animation-frame loop chases the end on a critically damped spring, and
 * re-reads where the end IS on every frame. A line landing mid-glide only moves
 * the target; the spring keeps its speed, so a burst reads as one continuous
 * slide rather than a jerk per line. A `ResizeObserver` on the content and the
 * viewport wakes it for every height change that is not a new line — Wrap, a
 * row being drawn, the filter summary appearing above the log.
 */

/** Nearer the end than this and the newest line is already in view. */
const EDGE_PX = 2

/**
 * How near the end the operator's own scrolling has to land to resume.
 *
 * Generous, because nobody should have to land a trackpad flick within a pixel
 * of the end to get the live view back.
 */
const RESUME_PX = 48

/**
 * How long after a wheel tick, a key or a finger the scrolling that follows
 * still counts as the operator's. A key's smooth scroll is the longest of them.
 */
const STEER_MS = 400

/** The same after a finger lifts, because a fling keeps scrolling on its own. */
const FLING_MS = 1_000

/**
 * After a jump — the drawer opening, a filter, Wrap — keep jumping rather than
 * gliding for this long.
 *
 * A jump lands on rows that have only ever been guessed at, and they are drawn
 * at their real height over the next frame or two. Gliding that correction
 * reads as the log drifting on its own; snapping it is invisible.
 */
const SETTLE_MS = 400

/**
 * The spring's stiffness, per millisecond: ω of 20 rad/s.
 *
 * Critically damped, so it never overshoots. At this stiffness a new line is
 * nine-tenths of the way into view in about 200ms and settled by 350.
 */
const GLIDE_W = 0.02

/**
 * Further than this many screens from the end, and the glide LANDS instead: it
 * jumps to half a screen short and glides the rest, arriving already slowing.
 * Sliding through a thousand lines is not smooth, it is a blur.
 */
const LAND_SCREENS = 1.5

/** Read live, so changing the OS setting takes effect on the next frame. */
const REDUCED_MOTION: MediaQueryList | null =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null

function distanceToEnd(el: HTMLElement): number {
  return el.scrollHeight - el.clientHeight - el.scrollTop
}

/**
 * The follow engine, outside React.
 *
 * It runs on scroll events, wheel ticks and animation frames, and nothing it
 * tracks is rendered — so it lives in a closure, and React hears about only
 * the two facts the screen shows: whether it is following, and whether the view
 * is at the end.
 */
function createTail(
  scroller: () => HTMLDivElement | null,
  onFollowing: (following: boolean) => void,
  onAtEnd: (atEnd: boolean) => void
) {
  let following = true
  let frame = 0
  let lastFrameAt = 0
  /** px per ms, positive towards the end. */
  let velocity = 0
  let settleUntil = 0
  let steerUntil = 0
  let steerDown = false
  /** The scrollbar is held. */
  let dragging = false
  /** A button is down on the lines — usually someone selecting text. */
  let holding = false
  let touchY = 0
  /**
   * The geometry as last seen — by a scroll event, or by the engine's own
   * write. Whatever differs from it at the next scroll event was not the
   * engine.
   */
  let seen = { top: 0, height: 0, view: 0 }

  const measure = (el: HTMLElement) => ({
    top: el.scrollTop,
    height: el.scrollHeight,
    view: el.clientHeight,
  })

  /** Every scroll the engine makes goes through here, so none is mistaken for the operator's. */
  const write = (el: HTMLElement, top: number): void => {
    el.scrollTop = top
    seen = measure(el)
  }

  /*
   * Scroll anchoring OFF while following, on while reading.
   *
   * Reading, the browser's anchoring is what holds the line under the eye
   * still while the ring drops old lines off the top. Following, the engine
   * anchors for itself (`shift`): the browser's adjustments are scrolls it
   * cannot tell from the operator's, and an unexplained move up is exactly
   * what `onScroll` takes as somebody going back to read. Set here rather
   * than through React so the rule changes in the same instant as the mode.
   */
  const setAnchoring = (el: HTMLElement | null): void => {
    if (el !== null) el.style.overflowAnchor = following ? 'none' : 'auto'
  }

  const setMode = (on: boolean): void => {
    following = on
    setAnchoring(scroller())
    onFollowing(on)
  }

  const rest = (): void => {
    if (frame !== 0) cancelAnimationFrame(frame)
    frame = 0
    lastFrameAt = 0
    velocity = 0
  }

  const reportEdge = (): void => {
    const el = scroller()
    if (el !== null) onAtEnd(distanceToEnd(el) <= EDGE_PX)
  }

  const step = (now: number): void => {
    frame = 0
    const el = scroller()
    // Held still under a pressed button; letting go wakes it again.
    if (el === null || !following || dragging || holding) return rest()

    const max = el.scrollHeight - el.clientHeight
    const top = el.scrollTop
    if (max - top <= 0.5) {
      // Finish ON the end, not a device pixel short of it.
      if (top < max) write(el, max)
      return rest()
    }

    if (now < settleUntil || REDUCED_MOTION?.matches === true) {
      write(el, max)
      return rest()
    }

    let from = top
    const screen = el.clientHeight
    if (max - top > screen * LAND_SCREENS) {
      from = max - screen / 2
      // Already moving at exactly the speed that decays into the end.
      velocity = (GLIDE_W * screen) / 2
    }

    const dt = lastFrameAt === 0 ? 16 : Math.min(48, now - lastFrameAt)
    lastFrameAt = now

    /*
     * The closed form of a critically damped spring over `dt`, pulling `x` —
     * how far short of the end, so negative — to zero. Exact for any frame
     * length, so a dropped frame costs a longer step rather than a wobble.
     */
    const x = from - max
    const drive = (velocity + GLIDE_W * x) * dt
    const decay = Math.exp(-GLIDE_W * dt)
    velocity = (velocity - GLIDE_W * drive) * decay
    // At least a pixel a frame: the spring's tail is asymptotic, and a
    // sub-pixel step can round to no movement at all.
    write(el, Math.min(max, Math.max(max + (x + drive) * decay, from + 1)))

    // Within half a pixel, or clamped just short by rounding: finish on the end.
    if (max - el.scrollTop <= 0.5 || Math.abs(el.scrollTop - top) < 0.25) {
      write(el, max)
      return rest()
    }
    frame = requestAnimationFrame(step)
  }

  /** Something changed height. Following, chase the end; reading, re-check it. */
  const wake = (): void => {
    if (!following) return reportEdge()
    if (frame === 0) frame = requestAnimationFrame(step)
  }

  const pause = (): void => {
    if (!following) return
    rest()
    setMode(false)
    reportEdge()
  }

  const follow = (): void => {
    settleUntil = 0
    if (!following) setMode(true)
    wake()
  }

  /** Straight to the end, before the frame is painted. */
  const jump = (): void => {
    if (!following) return reportEdge()
    rest()
    settleUntil = performance.now() + SETTLE_MS
    const el = scroller()
    if (el !== null && !dragging && !holding) write(el, el.scrollHeight)
  }

  /**
   * Rows above the view moved by `delta` px — the ring dropping its oldest
   * lines. This is the anchoring the browser is not doing while following:
   * the same move keeps the lines on screen still, and the glide then brings
   * the new ones in from below instead of the whole log hopping up a row.
   *
   * From `seen`, not the live position, because the removal may already have
   * clamped the live one partway there.
   */
  const shift = (delta: number): void => {
    const el = scroller()
    if (!following || el === null || Math.abs(delta) < 0.5) return
    write(el, seen.top + delta)
  }

  /** Once the scroller exists: the anchoring rule has to hold from the first frame. */
  const attach = (): void => {
    const el = scroller()
    setAnchoring(el)
    if (el !== null) seen = measure(el)
  }

  /** The operator is scrolling, and which way. Up is going back to read. */
  const steer = (down: boolean): void => {
    steerDown = down
    steerUntil = performance.now() + STEER_MS
    const el = scroller()
    if (!down && el !== null && el.scrollHeight - el.clientHeight > EDGE_PX) pause()
  }

  const onScroll = (): void => {
    const el = scroller()
    if (el === null) return
    const geo = measure(el)
    const moved = geo.top - seen.top
    /*
     * Up, and neither the engine nor the page explains it.
     *
     * The engine's own moves are already in `seen`. The page moves the view
     * up only by clamping — the content got shorter or the viewport taller —
     * and both show in the geometry. Anything else that went up was a person:
     * a scrollbar dragged in a browser that does not report the press,
     * middle-click autoscroll, find-in-page landing on an older line. That
     * holds only because the browser's anchoring is off while following; see
     * `setAnchoring`.
     */
    const pushedUp = moved < -1 && geo.height >= seen.height && geo.view <= seen.view
    seen = geo
    if (following) {
      if (pushedUp) pause()
      return
    }
    const steering = dragging || performance.now() < steerUntil
    // A dragged scrollbar says which way it went only by where it went.
    const down = dragging ? moved > 0 : steerDown
    if (steering && down && distanceToEnd(el) <= RESUME_PX) follow()
    else reportEdge()
  }

  const onWheel = (e: WheelEvent): void => {
    // Pinch-zoom arrives as a wheel with ctrl held; a sideways scroll has no Y.
    if (e.ctrlKey || e.deltaY === 0) return
    steer(e.deltaY > 0)
  }

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.defaultPrevented) return
    switch (e.key) {
      case 'End':
        e.preventDefault()
        follow()
        return
      case 'ArrowUp':
      case 'PageUp':
      case 'Home':
        steer(false)
        return
      case 'ArrowDown':
      case 'PageDown':
        steer(true)
        return
      case ' ':
        // On a response toggle Space opens it; it is not scrolling.
        if (e.target instanceof Element && e.target.closest('summary') !== null) return
        steer(!e.shiftKey)
        return
      default:
        return
    }
  }

  const onTouchStart = (e: TouchEvent): void => {
    touchY = e.touches[0]?.clientY ?? 0
  }

  const onTouchMove = (e: TouchEvent): void => {
    const y = e.touches[0]?.clientY
    if (y === undefined || Math.abs(y - touchY) < 4) return
    // A finger moving down drags the lines down: back towards older ones.
    steer(y < touchY)
    touchY = y
  }

  const onTouchEnd = (): void => {
    steerUntil = performance.now() + FLING_MS
  }

  const onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch' || e.button !== 0) return
    // On the scroller itself rather than a line inside it: the scrollbar.
    if (e.target === scroller()) dragging = true
    else holding = true
    rest()
  }

  const onRelease = (): void => {
    if (!dragging && !holding) return
    dragging = false
    holding = false
    wake()
  }

  const onFocusIn = (e: FocusEvent): void => {
    /*
     * Tabbing onto a line's response toggle is reading it. Keyboard focus
     * only: a click is judged by `onClick`, which knows whether the toggle is
     * opening or closing.
     */
    const t = e.target
    if (t !== scroller() && t instanceof Element && t.matches(':focus-visible')) pause()
  }

  const onClick = (e: MouseEvent): void => {
    const summary = e.target instanceof Element ? e.target.closest('summary') : null
    const details = summary?.parentElement
    // Opening a response body is reading it; the lines must not slide away under it.
    if (details instanceof HTMLDetailsElement && !details.open) pause()
  }

  return {
    attach,
    wake,
    jump,
    shift,
    follow,
    pause,
    onScroll,
    onWheel,
    onKeyDown,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onPointerDown,
    onRelease,
    onFocusIn,
    onClick,
    dispose: rest,
  }
}

type Tail = ReturnType<typeof createTail>

/** The newest line in view when following stopped; `unseen` counts past it. */
interface UnseenMark {
  line: RunLogLineDto | null
  at: number
}

/**
 * How many lines arrived after the mark.
 *
 * By position while the marked line is still in the list, which is exact. By
 * time once it is not — a filter can hide it and the ring can evict it — which
 * can only undercount, by lines written in the mark's own millisecond.
 */
function countSince(lines: readonly RunLogLineDto[], mark: UnseenMark | null): number {
  if (mark === null) return 0
  if (mark.line !== null) {
    const i = lines.lastIndexOf(mark.line)
    if (i !== -1) return lines.length - 1 - i
  }
  let n = 0
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const l = lines[i]
    if (l === undefined || l.at <= mark.at) break
    n += 1
  }
  return n
}

/**
 * The follow engine, wired to one log.
 *
 * `resetKey` names what the list is showing. When it changes, the lines were
 * REPLACED rather than added to — a filter, a search, Wrap — so a following
 * view jumps to the new end instead of gliding through a list it never showed.
 */
function useTail(visible: readonly RunLogLineDto[], resetKey: string) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  // On by default, every time the drawer opens: the live end is why it was opened.
  const [following, setFollowing] = React.useState(true)
  const [atEnd, setAtEnd] = React.useState(true)
  const [mark, setMark] = React.useState<UnseenMark | null>(null)
  const newest = React.useRef<RunLogLineDto | undefined>(undefined)

  const tailRef = React.useRef<Tail | null>(null)
  if (tailRef.current === null) {
    tailRef.current = createTail(
      () => scrollRef.current,
      (on) => {
        setFollowing(on)
        const line = newest.current
        setMark(on ? null : { line: line ?? null, at: line?.at ?? -Infinity })
      },
      setAtEnd
    )
  }
  const tail = tailRef.current

  /**
   * What the log showed at the last commit, for telling an append from a
   * replacement — and the engine's scroll anchor: the last row's element and
   * where it sat. On an append that row is still there, so any distance it has
   * moved is rows removed or resized ABOVE it, which `shift` compensates.
   */
  const shown = React.useRef<{
    key: string
    last: RunLogLineDto | undefined
    row: HTMLElement | null
    rowTop: number
  } | null>(null)

  // A layout effect, and declared first, so it is all in place before the
  // first jump below and the first paint.
  React.useLayoutEffect(() => {
    const el = scrollRef.current
    const content = contentRef.current
    if (el === null || content === null) return
    tail.attach()
    const passive = { passive: true } as const
    el.addEventListener('scroll', tail.onScroll, passive)
    el.addEventListener('wheel', tail.onWheel, passive)
    el.addEventListener('touchstart', tail.onTouchStart, passive)
    el.addEventListener('touchmove', tail.onTouchMove, passive)
    el.addEventListener('touchend', tail.onTouchEnd, passive)
    el.addEventListener('touchcancel', tail.onTouchEnd, passive)
    el.addEventListener('keydown', tail.onKeyDown)
    el.addEventListener('pointerdown', tail.onPointerDown)
    el.addEventListener('focusin', tail.onFocusIn)
    el.addEventListener('click', tail.onClick)
    // On the window, because a drag selecting text is let go wherever it ends.
    window.addEventListener('pointerup', tail.onRelease)
    window.addEventListener('pointercancel', tail.onRelease)
    window.addEventListener('blur', tail.onRelease)
    /*
     * Every height change that is not a new line: Wrap, a row being drawn at
     * its real height, the viewport resizing. `wake` only schedules a frame,
     * so nothing moves inside the observer's own callback — moving there would
     * draw more rows, resize the content again, and trip the observer's loop
     * limit.
     *
     * The anchor is re-read here too. These changes are the glide's to absorb,
     * so the next commit's `shift` must measure from after them — otherwise
     * it would move the view for them a second time.
     */
    const ro = new ResizeObserver(() => {
      const s = shown.current
      if (s !== null && s.row !== null && s.row.isConnected) s.rowTop = s.row.offsetTop
      tail.wake()
    })
    ro.observe(el)
    ro.observe(content)
    return () => {
      el.removeEventListener('scroll', tail.onScroll)
      el.removeEventListener('wheel', tail.onWheel)
      el.removeEventListener('touchstart', tail.onTouchStart)
      el.removeEventListener('touchmove', tail.onTouchMove)
      el.removeEventListener('touchend', tail.onTouchEnd)
      el.removeEventListener('touchcancel', tail.onTouchEnd)
      el.removeEventListener('keydown', tail.onKeyDown)
      el.removeEventListener('pointerdown', tail.onPointerDown)
      el.removeEventListener('focusin', tail.onFocusIn)
      el.removeEventListener('click', tail.onClick)
      window.removeEventListener('pointerup', tail.onRelease)
      window.removeEventListener('pointercancel', tail.onRelease)
      window.removeEventListener('blur', tail.onRelease)
      ro.disconnect()
      tail.dispose()
    }
  }, [tail])

  /*
   * Before paint, so a jump is never seen as a frame at the wrong place.
   *
   * An append — the last line shown is still in the list — glides. Anything
   * else replaced what was on screen: the first load, a filter, a re-seed after
   * a reconnect. There is nothing to glide FROM, so it jumps.
   */
  React.useLayoutEffect(() => {
    const before = shown.current
    const last = visible[visible.length - 1]
    newest.current = last
    const appended =
      before !== null &&
      before.key === resetKey &&
      before.last !== undefined &&
      visible.lastIndexOf(before.last) !== -1
    if (appended) {
      if (before.row !== null && before.row.isConnected) tail.shift(before.row.offsetTop - before.rowTop)
      tail.wake()
    } else {
      tail.jump()
    }
    const row = contentRef.current?.lastElementChild
    shown.current =
      row instanceof HTMLElement
        ? { key: resetKey, last, row, rowTop: row.offsetTop }
        : { key: resetKey, last, row: null, rowTop: 0 }
  }, [visible, resetKey, tail])

  const unseen = React.useMemo(() => countSince(visible, mark), [visible, mark])

  return {
    following,
    atEnd,
    unseen,
    scrollRef,
    contentRef,
    follow: tail.follow,
    pause: tail.pause,
  }
}

/* ================================================================== *
 * The console
 * ================================================================== */

export interface RunConsoleProps {
  lines: readonly RunLogLineDto[]
  state: RunStateDto | null
  loading: boolean
  /** Rendered above the log when a start was rejected. */
  error?: string | null
  className?: string
}

export function RunConsole({ lines, state, loading, error, className }: RunConsoleProps) {
  const [wrap, setWrap] = React.useState(true)
  const [filter, setFilter] = React.useState<LevelFilter>('all')
  /**
   * Which step to show, or null for all of them.
   *
   * Added because the log now mixes two call streams that run four orders of
   * magnitude apart in volume: the schedule watch emits a line a minute, the
   * application calls one an hour. Reading either on its own used to mean
   * reading past the other, and the search box could not do it — a free-text
   * query over messages is not the same as selecting a step, and gets it wrong
   * the moment anyone rewords a message.
   */
  const [step, setStep] = React.useState<string>(ALL_STEPS)
  /**
   * Which outcome of an update call to show, while `step` is `UPDATE_CALLS`.
   *
   * Held separately rather than folded into `step` as three more sentinels: it
   * is a second question asked ABOUT the first answer, its chips appear only
   * once the first is chosen, and merging the two would put "Failed update" in
   * the same radio group as "Sign-in" — one control offering two unrelated
   * kinds of choice, where arrowing right from a phase lands on an error code.
   */
  const [updateCode, setUpdateCode] = React.useState<UpdateCode>('all')
  const [query, setQuery] = React.useState('')
  const [copied, setCopied] = React.useState(false)

  /*
   * Stamped during render rather than in an effect, because the rows read the
   * stamps as they mount and an effect would run after they had decided. The
   * same idempotent-cache shape as `haystack`: a second pass over the same
   * lines finds every one stamped and does nothing.
   */
  React.useMemo(() => noteArrivals(lines), [lines])

  /** Cleared on unmount, so closing the drawer inside the 1.6s window is safe. */
  const copiedTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  React.useEffect(
    () => () => {
      if (copiedTimer.current !== null) clearTimeout(copiedTimer.current)
    },
    []
  )

  /**
   * The step groups this log actually contains, with their line counts.
   *
   * Derived rather than declared: a run that never hunted has no watch lines and
   * must not be offered a chip that filters to nothing. Unknown keys — a step
   * added on the server before this list learns about it — are collected into
   * their own chips at the end rather than silently vanishing from the filter,
   * which would make the chips' counts stop adding up to `All`.
   */
  const groups = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const l of lines) {
      if (l.stepKey === null) continue
      const label = stepChip(l.stepKey)
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
    const known = STEP_GROUPS.filter((g) => counts.has(g.label)).map((g) => ({
      label: g.label,
      n: counts.get(g.label) ?? 0,
    }))
    const knownLabels = new Set(known.map((g) => g.label))
    const extra = [...counts.entries()]
      .filter(([label]) => !knownLabels.has(label))
      .map(([label, n]) => ({ label, n }))
    return [...known, ...extra]
  }, [lines])

  /*
   * The filter runs on a DEFERRED copy of the query, not the live one.
   *
   * The input stays bound to `query`, so typing is always immediate. What is
   * deferred is the work: re-filtering the whole buffer, which now searches each
   * line's response body as well. At the buffer cap that is a scan of every
   * line's message, api, detail and status — per keystroke, on the main thread,
   * while the drawer is also taking a live push every 80ms.
   *
   * `useDeferredValue` rather than a timer because React can then interrupt the
   * filter when another keystroke arrives, instead of finishing a pass whose
   * result is already stale. No debounce constant to tune, and the last
   * keystroke is never the one that gets dropped.
   */
  const deferredQuery = React.useDeferredValue(query)

  const visible = React.useMemo(() => {
    const q = deferredQuery.trim().toLowerCase()
    if (filter === 'all' && step === ALL_STEPS && q === '') return lines

    return lines.filter((l) => {
      if (step === UPDATE_CALLS) {
        // The endpoint, then the outcome. Both read off the line itself: `api`
        // for the call, the response body for the code it came back with.
        if (!isUpdateCall(l)) return false
        if (updateCode !== 'all' && !haystack(l).includes(UPDATE_CODE_MATCH[updateCode])) {
          return false
        }
      } else if (step !== ALL_STEPS && (l.stepKey === null || stepChip(l.stepKey) !== step)) {
        // Matched on the GROUP the key belongs to, so one "Sign-in" chip covers
        // all four of the keys that phase emits.
        return false
      }
      if (filter === 'problems') {
        if (l.level !== 'warn' && l.level !== 'error') return false
      } else if (filter !== 'all' && l.level !== filter) {
        return false
      }
      if (q === '') return true
      /*
       * The search covers the RESPONSE too, not just the message.
       *
       * `detail` now holds the redacted body of every call, which makes it the
       * only place an upstream `errorCode`, an applicationId or a state string
       * actually appears. Searching a log for the code the API returned and
       * getting no hits — while it sits one click away inside a row — is the
       * kind of small dishonesty that makes a filter untrustworthy.
       */
      return haystack(l).includes(q)
    })
  }, [lines, filter, step, updateCode, deferredQuery])

  /**
   * Every chip's count in ONE pass over the log.
   *
   * Six chips each reducing the array independently is six passes that all
   * invalidate together on any append; this recomputes them as a group, keyed on
   * the same dependency, so a run that appends a line every 60s for hours does
   * not walk the buffer six times to do it.
   */
  const counts = React.useMemo(() => {
    const c: Record<LevelFilter, number> = {
      all: lines.length,
      problems: 0,
      info: 0,
      success: 0,
      warn: 0,
      error: 0,
    }
    for (const l of lines) {
      c[l.level] += 1
      if (l.level === 'warn' || l.level === 'error') c.problems += 1
    }
    return c
  }, [lines])

  /**
   * The severity chips, as options.
   *
   * An empty per-level chip is DROPPED rather than shown at zero: six chips of
   * which four read "0" is a row of noise, and in a radio group it is also four
   * extra arrow presses to cross. The two aggregates always stay, so the group
   * never collapses to nothing and `All` is always there to return to.
   */
  const levelOptions = React.useMemo<ChipOption[]>(
    () =>
      LEVEL_FILTERS.filter((f) => counts[f] > 0 || f === 'all' || f === 'problems').map((f) => {
        const n = counts[f]
        const tone = n > 0 ? FILTER_TONE[f] : undefined
        return { id: f, label: FILTER_LABEL[f], count: n, ...(tone ? { tone } : {}) }
      }),
    [counts]
  )

  /**
   * The update calls, and the two outcomes worth their own chip — one pass.
   *
   * Same rule as the severity counts above: three reductions over the same
   * array, keyed on the same dependency, done once. `haystack` is cached per
   * line in a `WeakMap`, so the code match costs a substring search on a string
   * the search box has usually built already.
   */
  const updateCounts = React.useMemo(() => {
    const c: Record<UpdateCode, number> = { all: 0, failed_update: 0, not_available: 0 }
    for (const l of lines) {
      if (!isUpdateCall(l)) continue
      c.all += 1
      const hay = haystack(l)
      if (hay.includes(UPDATE_CODE_MATCH.failed_update)) c.failed_update += 1
      if (hay.includes(UPDATE_CODE_MATCH.not_available)) c.not_available += 1
    }
    return c
  }, [lines])

  const stepOptions = React.useMemo<ChipOption[]>(() => {
    const out: ChipOption[] = [
      { id: ALL_STEPS, label: 'All steps', count: null },
      ...groups.map((g) => ({ id: g.label, label: g.label, count: g.n })),
    ]
    /*
     * Last, and only once there is one to show.
     *
     * The chips before it are the run's phases in the order the run performs
     * them; this one is a different question entirely, so it goes at the end
     * rather than in the middle of that sequence. Hidden at zero because a run
     * that has not reached the application yet would otherwise be offered a
     * chip that filters to nothing.
     */
    if (updateCounts.all > 0) {
      out.push({
        id: UPDATE_CALLS,
        label: 'Update calls',
        count: updateCounts.all,
        title: UPDATE_CODE_TITLE.all,
      })
    }
    return out
  }, [groups, updateCounts])

  /**
   * The outcome chips, shown only while the update-call filter is selected.
   *
   * The two codes keep their chips at zero rather than being dropped the way an
   * empty severity chip is, and the difference is deliberate: "no call has ever
   * been refused" is the answer somebody is looking for when they open this,
   * and a missing chip does not say it — it reads as a filter that is not
   * available yet.
   */
  const updateOptions = React.useMemo<ChipOption[]>(
    () =>
      (['all', 'failed_update', 'not_available'] as const).map((id) => {
        const n = updateCounts[id]
        return {
          id,
          label: UPDATE_CODE_LABEL[id],
          count: n,
          title: UPDATE_CODE_TITLE[id],
          // Toneless at zero, on the same rule as the severity chips: colouring
          // an empty chip trains the eye to see alarm where there is none.
          ...(id !== 'all' && n > 0 ? { tone: 'warn' as const } : {}),
        }
      }),
    [updateCounts]
  )

  /*
   * Selecting a severity that no longer exists would show an empty log.
   *
   * The chips are derived from the lines, and the lines move: a run whose only
   * error scrolls out of the 300-line ring drops the `Error` chip while it is
   * still the selected one, leaving a filter the operator can see is active but
   * can no longer see the control for. Falling back to `all` is the only state
   * that is always reachable.
   */
  React.useEffect(() => {
    if (!levelOptions.some((o) => o.id === filter)) setFilter('all')
  }, [levelOptions, filter])

  React.useEffect(() => {
    if (stepOptions.some((o) => o.id === step)) return
    // Same fallback as the severity chips above, and it takes the outcome
    // sub-filter with it: the update chip vanishes when its last call scrolls
    // out of the ring, and leaving `updateCode` set would arm a filter whose
    // control is no longer on screen.
    setStep(ALL_STEPS)
    setUpdateCode('all')
  }, [stepOptions, step])

  const onLevel = React.useCallback((id: string) => setFilter(id as LevelFilter), [])

  /**
   * Choosing a step clears the outcome sub-filter.
   *
   * The outcome chips only exist while `UPDATE_CALLS` is selected, so leaving
   * it while one was chosen would hide an active filter — a log showing a
   * fraction of its lines with nothing on screen saying why. The reverse case
   * needs no handling: arriving at the update chip always starts at `All`.
   */
  const onStep = React.useCallback((id: string) => {
    setStep(id)
    if (id !== UPDATE_CALLS) setUpdateCode('all')
  }, [])

  const onUpdateCode = React.useCallback((id: string) => setUpdateCode(id as UpdateCode), [])

  /*
   * Stick to the newest line only while the operator has not scrolled away.
   *
   * Auto-scrolling a log someone is reading is the worst thing a log viewer can
   * do, so the toggle is the INTENT and the operator's own scrolling is the
   * override: scrolling up pauses it, scrolling back down to the end resumes
   * it. `useTail` explains why that has to be the operator's scrolling and
   * never just any scroll event.
   */
  const tail = useTail(
    visible,
    `${filter}|${step}|${updateCode}|${deferredQuery}|${wrap ? 'wrap' : 'cut'}`
  )
  const { follow, scrollRef } = tail

  const onJump = React.useCallback(() => {
    follow()
    // The button hides itself as it is pressed; give focus to the log rather
    // than let it fall to the page.
    scrollRef.current?.focus({ preventScroll: true })
  }, [follow, scrollRef])

  /**
   * The visible log as text, shared by Copy and Download.
   *
   * One definition rather than two, because the two diverging is how a
   * downloaded log ends up missing the field someone copied a moment earlier and
   * then cannot find. Response bodies are INDENTED beneath their row instead of
   * jammed into the tab-separated line: a JSON blob with tabs in it would break
   * the columns for every other row in the file.
   */
  const asText = React.useCallback(
    (rows: readonly RunLogLineDto[]): string =>
      rows
        .map((l) => {
          const head = [
            new Date(l.at).toISOString(),
            l.level.toUpperCase(),
            l.stepKey ?? '-',
            // The endpoint travels with the line, so a log read outside this
            // console does not have to infer which call each row describes —
            // which is exactly what could not be inferred inside it either.
            typeof l.api === 'string' ? l.api : '-',
            l.message,
            l.smallDetail ?? '',
            // The numbers come along, so a log pasted into a ticket still answers
            // "which call was slow and what did it return".
            typeof l.httpStatus === 'number' ? String(l.httpStatus) : '',
            typeof l.ms === 'number' ? `${l.ms}ms` : '',
          ]
            .join('\t')
            .trimEnd()
          if (typeof l.detail !== 'string' || l.detail === '') return head
          const body = l.detail
            .split('\n')
            .map((ln) => `    ${ln}`)
            .join('\n')
          return `${head}\n${body}`
        })
        .join('\n'),
    []
  )

  const onDownload = React.useCallback(() => {
    const blob = new Blob([asText(visible)], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    // Named by run and time, because the first thing anyone does with two of
    // these is compare them, and "log.txt (3)" makes that guesswork.
    const runId = visible[0]?.runId ?? 'run'
    a.download = `run-${runId.slice(0, 8)}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.txt`
    a.click()
    // Revoked on the next tick rather than immediately: revoking synchronously
    // after click() races the browser's own fetch of the blob in some engines,
    // and the download silently produces an empty file.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }, [visible, asText])

  const onCopy = React.useCallback(() => {
    const text = asText(visible)

    void navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true)
        if (copiedTimer.current !== null) clearTimeout(copiedTimer.current)
        copiedTimer.current = setTimeout(() => setCopied(false), 1600)
      },
      () => {
        /* clipboard denied — the button simply does not confirm */
      }
    )
  }, [visible, asText])

  return (
    /*
     * A CARD, matching the hunt panel beside it.
     *
     * These two are now columns of equal weight, and a bordered panel next to a
     * set of loose elements reads as one finished thing and one unfinished one.
     * The border also gives the strip and the log a shared edge to align to,
     * which is what stops the counter tiles floating a few pixels off the log
     * beneath them.
     */
    <section
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-md border border-line bg-s1',
        className
      )}
    >
      <header className="flex flex-none items-center gap-2 border-b border-line px-3 py-2">
        <Terminal aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
        <span className="text-[12.5px] text-fg">This customer&rsquo;s calls</span>
        <span className="ml-auto truncate text-[11px] text-faint">
          sign-in · application · keep-alive
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-2.5">
      {/* The readout operators already know, kept: five phases and one line of
          plain English, above the numbers. */}
      {state ? <RunPipeline state={state} /> : null}
      {state ? <RunCounters state={state} /> : null}

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
          <AlertTriangle aria-hidden="true" className="mt-px size-4 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium">Could not start</div>
            <div className="break-words opacity-90">{error}</div>
          </div>
        </div>
      ) : null}

      {/* -------- controls --------
          Two rows, not one.

          Severity and step are different questions and they combine, so putting
          them on one wrapping line made the wrap point depend on how many chips
          each happened to have — which is how "Keep-alive" ended up hyphenated
          across two lines beside a search box squeezed to nothing. Filters on
          their own row, the search and the actions on the next, and neither can
          push the other around. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <ChipGroup label="Filter by severity" options={levelOptions} value={filter} onChange={onLevel} />
          {/*
            One chip per phase of the run, plus the endpoint chip at the end.

            Hidden below two phases, because on a run that has not started
            hunting yet a group where every option selects the same lines is
            furniture — unless the update-call chip is there, which is a real
            second choice however few phases have run.
          */}
          {groups.length > 1 || updateCounts.all > 0 ? (
            <ChipGroup
              label="Filter by step or endpoint"
              options={stepOptions}
              value={step}
              onChange={onStep}
            />
          ) : null}
          {/*
            The second question, asked only once the first has been answered.

            Every update call, or only the ones the endpoint refused — and the
            two refusals are not the same finding. `FAILED_UPDATE_APPLICATION`
            is the endpoint declining the update itself, which the claim
            stand-down deliberately ignores; `SELECTED_SCHEDULE_NOT_AVAILABLE`
            is somebody else having taken the shift, which is a lost race and
            not a fault. They arrive as the same HTTP 200 and read as the same
            warn line, so nothing else on this screen separates them.
          */}
          {step === UPDATE_CALLS ? (
            <ChipGroup
              label="Filter update calls by what came back"
              options={updateOptions}
              value={updateCode}
              onChange={onUpdateCode}
            />
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
        <label className="relative flex min-w-0 flex-1 items-center sm:max-w-[16rem]">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2 size-3.5 text-faint"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter lines…"
            aria-label="Filter log lines"
            className={cn(
              'h-7 w-full rounded-md border border-line bg-s1 pl-7 pr-2 text-[12.5px] text-fg',
              'placeholder:text-faint focus:border-accent focus:outline-none'
            )}
          />
        </label>

        <div className="ml-auto flex items-center gap-1">
          <Toggle
            active={tail.following}
            onClick={tail.following ? tail.pause : tail.follow}
            title={
              tail.following
                ? 'Following new lines as they arrive. Scroll up, or click, to pause.'
                : 'Paused. Click, or press End in the log, to follow new lines again.'
            }
          >
            <ArrowDownToLine aria-hidden="true" className="size-3.5" />
            <span className="hidden sm:inline">Auto-scroll</span>
          </Toggle>

          <Toggle
            active={wrap}
            onClick={() => setWrap((v) => !v)}
            title={wrap ? 'Wrapping long lines' : 'Truncating long lines'}
          >
            <WrapText aria-hidden="true" className="size-3.5" />
            <span className="hidden sm:inline">Wrap</span>
          </Toggle>

          <Button
            variant="ghost"
            size="sm"
            onClick={onCopy}
            disabled={visible.length === 0}
            title="Copy the visible lines, with every response body"
          >
            {copied ? (
              <Check aria-hidden="true" className="size-3.5 text-ok" />
            ) : (
              <Copy aria-hidden="true" className="size-3.5" />
            )}
            <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
          </Button>

          {/* Copy is for pasting into a message; this is for keeping. A run's
              full request/response trace is the attachment a bug report wants,
              and it is far too long to paste. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDownload}
            disabled={visible.length === 0}
            title="Download the visible lines as a text file"
          >
            <Download aria-hidden="true" className="size-3.5" />
            <span className="sr-only">Download</span>
          </Button>
        </div>
        </div>
      </div>

      {/*
        What the filters are currently hiding, said out loud.

        Only when something IS hidden — an unfiltered log does not need to
        announce that it is showing everything. `role="status"` makes it the
        one polite announcement a keyboard user gets after changing a chip;
        the log region below announces appended LINES, which is the wrong
        thing to hear when what changed was the filter.
      */}
      {visible.length !== lines.length ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 text-[11.5px] text-faint"
        >
          <span>
            Showing {NUM.format(visible.length)} of {NUM.format(lines.length)} lines
          </span>
          <button
            type="button"
            onClick={() => {
              setQuery('')
              setFilter('all')
              setStep(ALL_STEPS)
            }}
            className="rounded-xs px-1 text-accent hover:bg-s2 focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none"
          >
            Clear filters
          </button>
        </div>
      ) : null}

      {/* -------- the log --------
          In a positioned frame, so the jump button floats over the lines
          without scrolling away with them. */}
      <div className="relative flex min-h-[14rem] flex-1 flex-col">
      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-label="Run log"
        /*
         * Focusable, so a keyboard can scroll it and End can bring back the
         * newest line. Without it the response toggles inside are the only
         * stops, and tabbing through those is not reading a log.
         */
        tabIndex={0}
        className={cn(
          'min-h-0 flex-1 overflow-auto overscroll-contain rounded-md border border-line bg-s1',
          'font-mono text-[12px] leading-[1.65]'
        )}
      >
        {/* Observed as one box, so a new line, a row being drawn and Wrap
            all reach the engine as the height they changed. `relative` makes
            it the rows' offsetParent, so a row's `offsetTop` is its place in
            the log and not in the scrolled page. */}
        <div ref={tail.contentRef} className="relative">
        {visible.length > 0 ? (
          visible.map((line) => (
            /*
             * Keyed by CONTENT, not by index.
             *
             * The ring has no ids, and an index key is wrong here in two ways
             * that both actually happen: the 300-line cap slides from the front,
             * which shifts every index and remounts all 300 rows on one append;
             * and the index is relative to the FILTERED array, so toggling a
             * filter chip remounts everything again.
             *
             * `at|step|message` is the same tuple the append de-duplication
             * treats as identity, so two lines that collide here are two lines
             * the cache would already have merged into one.
             */
            <LogRow
              key={`${line.at}|${line.stepKey ?? ''}|${line.message}`}
              line={line}
              wrap={wrap}
              query={query.trim()}
            />
          ))
        ) : (
          <Empty
            loading={loading}
            filtered={lines.length > 0}
            phase={state?.phase ?? null}
            onClear={() => {
              setQuery('')
              setFilter('all')
              setStep(ALL_STEPS)
            }}
          />
        )}
        </div>
      </div>
      <JumpToLatest show={!tail.following && !tail.atEnd} unseen={tail.unseen} onJump={onJump} />
      </div>
      </div>
    </section>
  )
}

/* ================================================================== *
 * Small parts
 * ================================================================== */

/**
 * Tone applies to the INACTIVE state only.
 *
 * A selected chip is already marked by its filled background, and tinting the
 * text on top of that reduces contrast against it for no added information. The
 * colour is there to draw the eye to a chip worth clicking, which is precisely
 * the chip that is not currently selected.
 */
const CHIP_TONE: Readonly<Record<'warn' | 'danger' | 'ok', string>> = Object.freeze({
  warn: 'text-warn',
  danger: 'text-danger',
  ok: 'text-ok',
})

/**
 * One option in a `ChipGroup`. Never rendered on its own.
 *
 * `count` is `null` for an option with nothing to count rather than 0, because
 * the two mean different things here: a chip reading "Warn 0" is a promise that
 * there are no warnings, and a chip with no number at all is one that does not
 * count.
 */
interface ChipOption {
  id: string
  label: string
  count: number | null
  tone?: 'warn' | 'danger' | 'ok'
  /**
   * Hover text, for a chip whose label is prose but whose meaning is exact.
   *
   * The update-call outcomes are upstream `errorCode` constants; "Schedule not
   * available" is what a reader wants on the chip and
   * `SELECTED_SCHEDULE_NOT_AVAILABLE` is what they need in order to match it
   * against a response body. Both, rather than choosing.
   */
  title?: string
}

/**
 * A single-choice filter, as a real radio group.
 *
 * ── Why not a row of toggle buttons ─────────────────────────────────
 * These chips are mutually exclusive — picking one deselects the rest — and
 * `aria-pressed` says the opposite: it describes each button as independently on
 * or off, so a screen reader announces "All, pressed. Problems, not pressed.
 * Info, not pressed…" and never conveys that they are one choice. `radiogroup`
 * announces the group, the option, and its position within it, which is the
 * whole of what a filter needs to say.
 *
 * That semantic brings an obligation with it: a radio group is ONE tab stop, and
 * arrow keys move within it. Eleven chips as eleven tab stops is eleven presses
 * to reach the search box beside them. So the group carries a roving tabindex —
 * only the selected chip is tabbable — and Arrow/Home/End move and select
 * together, which is the standard behaviour for a radio group and what anyone
 * navigating by keyboard will already expect.
 */
function ChipGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly ChipOption[]
  value: string
  onChange: (id: string) => void
}) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  const move = (from: number, delta: number): void => {
    const next = (from + delta + options.length) % options.length
    const option = options[next]
    if (!option) return
    refs.current[next]?.focus()
    onChange(option.id)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(index, -1)
        break
      case 'Home':
        e.preventDefault()
        move(0, 0)
        break
      case 'End':
        e.preventDefault()
        move(0, options.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex flex-wrap items-center gap-0.5 rounded-md border border-line bg-s1 p-0.5"
    >
      {options.map((option, index) => {
        const active = option.id === value
        return (
          <button
            key={option.id}
            ref={(el) => {
              refs.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={active}
            {...(option.title === undefined ? {} : { title: option.title })}
            /*
             * The count is spelled out for a screen reader rather than left as a
             * bare number beside the label, which announces as "Warn 19" — two
             * unrelated values read as one string.
             */
            aria-label={option.count === null ? option.label : `${option.label}, ${option.count}`}
            tabIndex={active ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, index)}
            onClick={() => onChange(option.id)}
            className={cn(
              'flex h-6 items-center gap-1 rounded-sm px-2 text-[12px] whitespace-nowrap transition-colors',
              'focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none',
              active ? 'bg-s3 text-fg' : 'text-muted hover:bg-s2 hover:text-fg',
              option.tone !== undefined && !active && CHIP_TONE[option.tone]
            )}
          >
            <span>{option.label}</span>
            {option.count === null ? null : (
              <span aria-hidden="true" className="tabular text-[10.5px] text-faint">
                {NUM.format(option.count)}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

function Toggle({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={cn(
        'flex h-7 items-center gap-1.5 rounded-md border px-2 text-[12px] transition-colors',
        active
          ? 'border-accent/40 bg-accent-soft text-accent'
          : 'border-line bg-s1 text-muted hover:text-fg'
      )}
    >
      {children}
    </button>
  )
}

/**
 * The way back to the live end, shown only while the view is away from it.
 *
 * It floats over the log rather than living in the toolbar because that is
 * where the eye is when it matters: somebody scrolled up to read, and lines are
 * piling up below them. The count answers "is anything still happening?"
 * without leaving the line being read.
 *
 * Always mounted, so it can fade out as well as in; `inert` takes the hidden
 * one out of the tab order and away from the pointer.
 */
function JumpToLatest({
  show,
  unseen,
  onJump,
}: {
  show: boolean
  unseen: number
  onJump: () => void
}) {
  /*
   * The count it was showing, held while it fades out.
   *
   * Following again zeroes `unseen` in the same render that hides the button,
   * so without this the last frames of the fade would read "Jump to latest"
   * instead of the number that was just clicked.
   */
  const [held, setHeld] = React.useState(unseen)
  if (show && held !== unseen) setHeld(unseen)
  const n = show ? unseen : held

  return (
    <button
      type="button"
      onClick={onJump}
      inert={!show}
      aria-label={n > 0 ? `Jump to the newest line, ${NUM.format(n)} new` : 'Jump to the newest line'}
      title="Jump to the newest line and follow new ones (End)"
      className={cn(
        'absolute bottom-3 left-1/2 z-10 flex h-7 -translate-x-1/2 items-center gap-1.5 rounded-full',
        'bg-accent pr-3 pl-2.5 text-[12px] font-medium whitespace-nowrap text-accent-fg shadow-e2',
        'transition-[opacity,translate,background-color] duration-200 ease-out hover:bg-accent-hover',
        show ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
      )}
    >
      <ArrowDown aria-hidden="true" className="size-3.5" />
      {n > 0 ? (
        <span>
          {/* Keyed on the count, so each new line drops the number in again. */}
          <span key={n} className="log-tick tabular">
            {NUM.format(n)}
          </span>
          {n === 1 ? ' new line' : ' new lines'}
        </span>
      ) : (
        <span>Jump to latest</span>
      )}
    </button>
  )
}

function Empty({
  loading,
  filtered,
  phase,
  onClear,
}: {
  loading: boolean
  filtered: boolean
  phase: RunPhase | null
  onClear: () => void
}) {
  // Filtered-to-nothing is a different situation from having nothing, and it has
  // a fix — so it gets the button rather than the same shrug.
  if (filtered) {
    return (
      <div className="flex flex-col items-center gap-2 px-3 py-10 text-center font-sans text-[13px] text-muted">
        <span>No lines match this filter.</span>
        <Button variant="secondary" size="sm" onClick={onClear}>
          <X aria-hidden="true" className="size-3.5" />
          Clear filter
        </Button>
      </div>
    )
  }

  if (phase !== null && phase !== 'idle' && phase !== 'stopped' && phase !== 'failed') {
    return (
      <p className="px-3 py-10 text-center font-sans text-[13px] text-muted">
        Opening a secure session — the browser mints a token before the first call,
        so the opening events take a few seconds.
      </p>
    )
  }

  return (
    <p className="px-3 py-10 text-center font-sans text-[13px] text-muted">
      {loading ? 'Loading…' : 'No run recorded for this customer yet.'}
    </p>
  )
}
